import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import moment from "moment";
import { getLocalPhoneNumber } from "./helpers"; // Import the function

const insertSpacingBetweenCharacters = (string, spaces) => {
  if (!string) return "";
  return string
    .split("")
    .map((character, index) =>
      index < string.length - 1 ? character + " ".repeat(spaces) : character
    )
    .join("");
};

const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

const scaleCoordinate = (coord, pdfWidth, pdfHeight, axis) => {
  // Default A4 dimensions at 72 PPI
  const defaultWidth = 595.28;
  const defaultHeight = 841.89;

  const scale =
    axis === "x" ? pdfWidth / defaultWidth : pdfHeight / defaultHeight;
  return coord * scale;
};

const localizedValues = {
  maccabi: "מכבי",
  leumit: "לאומית",
  clalit: "כללית",
  meuhedet: "מאוחדת",
};

const localizeValue = (value) => {
  if (!value) return "";

  // Convert to lowercase for case-insensitive matching
  const lookupValue = value.toString().toLowerCase();

  // Return the Hebrew translation if it exists, otherwise return the original value
  return localizedValues[lookupValue] || value;
};

const addImageToPDF = async (
  pdfDoc,
  base64Image,
  x,
  y,
  pageIndex,
  spacing = 0
) => {
  try {
    const imageData = base64Image.replace(/^data:image\/\w+;base64,/, "");
    const imageBytes = Uint8Array.from(atob(imageData), (c) => c.charCodeAt(0));

    let image;
    if (base64Image.includes("image/jpeg")) {
      image = await pdfDoc.embedJpg(imageBytes);
    } else if (base64Image.includes("image/png")) {
      image = await pdfDoc.embedPng(imageBytes);
    } else {
      throw new Error("Unsupported image format");
    }

    const baseSize = 50;
    const maxHeight = 50; // Maximum height in points
    const scaleFactor = 1 + spacing * 0.5;
    const targetSize = baseSize * scaleFactor;

    const page = pdfDoc.getPages()[pageIndex];
    const scaledDims = image.scaleToFit(targetSize, targetSize);

    // Apply max height constraint while maintaining aspect ratio
    if (scaledDims.height > maxHeight) {
      const aspectRatio = scaledDims.width / scaledDims.height;
      scaledDims.height = maxHeight;
      scaledDims.width = maxHeight * aspectRatio;
    }

    // Calculate x position to center the image
    const centeredX = x - scaledDims.width / 2;

    page.drawImage(image, {
      x: centeredX,
      y: y - scaledDims.height,
      width: scaledDims.width,
      height: scaledDims.height,
    });
  } catch (error) {
    console.error("Error adding image to PDF:", error);
  }
};

const populatePDF = (pdfDoc, entity, data, customFont, config) => {
  const targetPage = pdfDoc.getPages()[0];
  const pdfWidth = targetPage.getWidth();
  const pdfHeight = targetPage.getHeight();

  const relevantConfig = config.filter((item) => item.entity === entity);

  relevantConfig.forEach((config) => {
    const {
      field,
      x,
      y,
      page,
      spacing,
      format,
      type,
      checkbox_config,
      list_config,
      value_type,
      value,
    } = config;

    // For const values, use the value directly
    let fieldValue =
      value_type === "const" ? value : getNestedValue(data, field);

    if (fieldValue === undefined || fieldValue === null) {
      console.warn(`Field ${field} is undefined or null for entity ${entity}`);
      return;
    }

    // Handle different field types
    if (type === "list" && list_config?.mapping) {
      // Handle list type fields
      const arrayData = fieldValue;
      if (!Array.isArray(arrayData)) {
        console.warn(`Field ${field} is not an array for entity ${entity}`);
        return;
      }

      // Iterate through each mapping coordinate and corresponding array item
      Object.entries(list_config.mapping).forEach(([subField, coordinates]) => {
        coordinates.forEach((coord, index) => {
          if (index < arrayData.length) {
            let itemValue = arrayData[index][subField];
            if (itemValue !== undefined) {
              const scaledX = scaleCoordinate(
                coord.x,
                pdfWidth,
                pdfHeight,
                "x"
              );
              const scaledY = scaleCoordinate(
                coord.y,
                pdfWidth,
                pdfHeight,
                "y"
              );

              // Apply standard formatting
              if (format === "phone") {
                itemValue = getLocalPhoneNumber(itemValue);
              } else if (format === "address") {
                itemValue = formatAddress(itemValue);
              } else if (format) {
                itemValue = moment(itemValue).format(format);
              }

              // Apply spacing if specified
              if (spacing) {
                itemValue = insertSpacingBetweenCharacters(
                  itemValue.toString(),
                  spacing
                );
              }

              addTextToPDF(
                pdfDoc,
                itemValue.toString(),
                scaledX,
                scaledY,
                page,
                customFont
              );
            }
          }
        });
      });
    } else if (type === "checkbox") {
      // Existing checkbox handling
      const markerValue = fieldValue.toString().toLowerCase();
      const markerCoordinates = checkbox_config?.mapping?.[markerValue];
      if (markerCoordinates) {
        const scaledX = scaleCoordinate(
          markerCoordinates.x,
          pdfWidth,
          pdfHeight,
          "x"
        );
        const scaledY = scaleCoordinate(
          markerCoordinates.y,
          pdfWidth,
          pdfHeight,
          "y"
        );
        addTextToPDF(pdfDoc, "√", scaledX, scaledY, page, customFont, 11);
      }
    } else {
      const scaledX = scaleCoordinate(x, pdfWidth, pdfHeight, "x");
      const scaledY = scaleCoordinate(y, pdfWidth, pdfHeight, "y");
      // Standard field handling with existing formatting
      if (format === "phone") {
        fieldValue = getLocalPhoneNumber(fieldValue);
      } else if (format === "address") {
        fieldValue = formatAddress(fieldValue);
      } else if (format === "localized") {
        fieldValue = localizeValue(fieldValue);
      } else if (format === "logo") {
        // Handle logo as image with spacing for size control
        addImageToPDF(pdfDoc, fieldValue, scaledX, scaledY, page, spacing || 0);
        return; // Skip text processing for logos
      } else if (format) {
        fieldValue = moment(fieldValue).format(format);
      }

      if (spacing) {
        fieldValue = insertSpacingBetweenCharacters(
          fieldValue.toString(),
          spacing
        );
      }

      addTextToPDF(
        pdfDoc,
        fieldValue.toString(),
        scaledX,
        scaledY,
        page,
        customFont
      );
    }
  });
};

const addTextToPDF = (pdfDoc, text, x, y, pageIndex, customFont, size = 9) => {
  if (text === undefined || text === null) {
    console.warn(
      `Attempted to add undefined or null text to PDF at coordinates (${x}, ${y})`
    );
    return; // Skip adding this text
  }

  const page = pdfDoc.getPages()[pageIndex];
  const textWidth = customFont.widthOfTextAtSize(text, size);
  const adjustedX = x - textWidth; // Adjust for RTL languages
  page.drawText(text, { x: adjustedX, y, size, font: customFont });
};

const formatAddress = (address) => {
  if (!address) return "";
  const { city, street, house_number } = address;
  return [street, house_number, city].filter(Boolean).join(", ");
};

export const fillCompanyForm = async (form, data) => {
  const fontUrl = process.env.PUBLIC_URL + "/Rubik-Medium.ttf";
  const fontBytes = await fetch(fontUrl).then((res) => res.arrayBuffer());

  // Convert base64 to array buffer
  const formPdfBytes = Uint8Array.from(atob(form.content), (c) =>
    c.charCodeAt(0)
  ).buffer;

  const pdfDoc = await PDFDocument.load(formPdfBytes);
  pdfDoc.registerFontkit(fontkit);
  const customFont = await pdfDoc.embedFont(fontBytes);

  // Process each entity in the data object
  Object.entries(data).forEach(([entity, entityData]) => {
    // Add formatted address if entity is company
    const processedData = {
      ...entityData,
      address_details: formatAddress(entityData.address),
      current_date: moment().format(),
      full_name: `${entityData.surename} ${entityData.first_name}`,
      full_name_english:
        entity === "employee" &&
        `${entityData?.foreign?.surename} ${entityData?.foreign?.surename}`,
    };

    // Get relevant config for this entity
    const relevantConfig = form.config.fields.filter(
      (item) => item.entity === entity
    );

    populatePDF(pdfDoc, entity, processedData, customFont, relevantConfig);
  });

  const constConfig = form.config.fields.filter(
    (item) => item.value_type === "const"
  );
  populatePDF(pdfDoc, "", {}, customFont, constConfig);

  const modifiedPdfBytes = await pdfDoc.save();
  const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
};

// You can remove or deprecate fillForm101 since fillCompanyForm will handle all cases
